import React, { memo, useMemo } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTable } from 'react-table';
import { formatUnixToDate } from '_utils/formatNumber';
import { GROUP_DONT_APPROVAL } from '_utils/constant';
import MemberAvatar from '../MemberAvatar';
import './style.scss';

const formatLastActive = (timestamp, fallbackTimestamp) => {
  const ts = timestamp || fallbackTimestamp;
  if (!ts) return '-';
  const dateObj = ts < 1e11 ? moment.unix(ts) : moment(ts);
  const now = moment();
  if (now.isSame(dateObj, 'day')) return 'Today';
  return dateObj.fromNow();
};

const TableMembers = ({
  data = [],
  onRowClick = null,
  onWhatsApp = null,
  onEdit = null,
  onBan = null,
  checkIsCurrentUser = null,
  checkIsOwner = null,
  canModify = null,
  canManage = false,
  onClickPending = null,
}) => {
  const columns = useMemo(
    () => [
      {
        Header: 'Join Date',
        accessor: 'createdAt',
        Cell: ({ value }) => (
          <span className="text-muted">{value ? formatUnixToDate(value) : '-'}</span>
        ),
      },
      {
        Header: 'Member',
        accessor: 'name',
        Cell: ({ row }) => {
          const member = row?.original;
          const displayName = member?.uName || member?.name || 'Member';
          const email = member?.email || '';

          return (
            <div className="avatar-wrap">
              <MemberAvatar member={member} name={displayName} className="avatar" />
              <div>
                <div className="member-name">{displayName}</div>
                {email && <div className="member-email">{email}</div>}
              </div>
            </div>
          );
        },
      },
      {
        Header: 'Events Hosted',
        accessor: 'totalEvents',
        Cell: ({ value }) => {
          const count = Number(value) || 0;
          return <span className={count > 0 ? 'text-teal' : 'text-muted'}>{count}</span>;
        },
      },
      {
        Header: 'Purchases',
        accessor: 'orderCount',
        Cell: ({ value }) => {
          const count = Number(value) || 0;
          return (
            <div>
              <span className="text-teal">{count}</span>
              <span className="text-muted" style={{ fontSize: '11px' }}> purchases</span>
            </div>
          );
        },
      },
      {
        Header: 'Last Active',
        accessor: 'lastModifiedAt',
        Cell: ({ value, row }) => {
          const createdAt = row?.original?.createdAt;
          return (
            <span className="text-muted" style={{ fontSize: '11px' }}>
              {formatLastActive(value, createdAt)}
            </span>
          );
        },
      },
      {
        Header: 'Permission',
        accessor: 'role',
        Cell: ({ row }) => {
          const member = row?.original;
          if (member?.banned) {
            return <span className="badge banned">BANNED</span>;
          }
          if (member?.approval === GROUP_DONT_APPROVAL) {
            if (onClickPending) {
              return (
                <button
                  type="button"
                  className="badge pending btn-pending"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClickPending(member);
                  }}
                >
                  Pending
                </button>
              );
            }
            return <span className="badge pending">Pending</span>;
          }
          const isOwner = typeof checkIsOwner === 'function' ? checkIsOwner(member) : false;
          if (isOwner) {
            return <span className="badge admin">Owner</span>;
          }
          if (member?.isAdmin && member?.isHost) {
            return <span className="badge admin">Admin</span>;
          }
          if (member?.isHost) {
            return <span className="badge host">Host</span>;
          }
          return <span className="badge member">Member</span>;
        },
      },
      // Chỉ admin/owner nhóm mới thấy cột thao tác
      canManage && {
        Header: '',
        id: 'actions',
        Cell: ({ row }) => {
          const member = row?.original;
          const isCurrent =
            typeof checkIsCurrentUser === 'function' ? checkIsCurrentUser(member) : false;
          const isOwner = typeof checkIsOwner === 'function' ? checkIsOwner(member) : false;
          const isModifiable =
            typeof canModify === 'function' ? canModify(member) : !isCurrent && !isOwner;
          const isBanned = Boolean(member?.banned === 1 || member?.isBanned);

          const handleWhatsApp = (e) => {
            e.stopPropagation();
            if (onWhatsApp) {
              onWhatsApp(member);
            } else if (member?.phone) {
              const cleanPhone = String(member.phone).replace(/\D/g, '');
              window.open(`https://wa.me/${cleanPhone}`, '_blank');
            }
          };

          const handleEdit = (e) => {
            e.stopPropagation();
            if (onEdit) onEdit(member);
          };

          const handleBan = (e) => {
            e.stopPropagation();
            if (onBan) onBan(member);
          };

          return (
            <div className="action-cell">
              {!isCurrent && Boolean(member?.phone) && (
                <button
                  type="button"
                  className="icon-btn whatsapp"
                  title="WhatsApp"
                  onClick={handleWhatsApp}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.122 1.523 5.855L.057 23.082a.75.75 0 0 0 .916.916l5.228-1.466A11.945 11.945 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.93 0-3.736-.518-5.29-1.424l-.378-.224-3.905 1.095 1.094-3.906-.224-.378A9.953 9.953 0 0 1 2 12C2 6.478 6.478 2 12 2s10 4.478 10 10-4.478 10-10 10z" />
                  </svg>
                </button>
              )}
              {typeof onEdit === 'function' && (
                <button
                  type="button"
                  className="icon-btn"
                  title="Edit"
                  onClick={handleEdit}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              )}
              {isModifiable && (
                <button
                  type="button"
                  className={`icon-btn ${isBanned ? '' : 'danger'}`}
                  title={isBanned ? 'Unban' : 'Ban'}
                  onClick={handleBan}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                  </svg>
                </button>
              )}
            </div>
          );
        },
      },
    ].filter(Boolean),
    [
      onWhatsApp,
      onEdit,
      onBan,
      checkIsCurrentUser,
      checkIsOwner,
      canModify,
      onClickPending,
      canManage,
    ]
  );

  const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: tableData,
  });

  return (
    <div
      className={`table-members${typeof onRowClick === 'function' ? '' : ' is-readonly'}`}
    >
      <div className="table-wrap">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, ...headerGroupProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={headerGroupKey} {...headerGroupProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: columnKey, ...columnProps } = column.getHeaderProps();
                    return (
                      <th key={columnKey} {...columnProps}>
                        {column.render('Header')}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-state">
                  No members found
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr
                    key={rowKey}
                    {...rowProps}
                    onClick={() => onRowClick && onRowClick(row.original)}
                  >
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } = cell.getCellProps();
                      return (
                        <td key={cellKey} {...cellProps}>
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

TableMembers.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onRowClick: PropTypes.func,
  onWhatsApp: PropTypes.func,
  onEdit: PropTypes.func,
  onBan: PropTypes.func,
  checkIsCurrentUser: PropTypes.func,
  checkIsOwner: PropTypes.func,
  canModify: PropTypes.func,
  canManage: PropTypes.bool,
  onClickPending: PropTypes.func,
};

export default memo(TableMembers);
